import {
  AutoComplete,
  DateInput,
  FormInput,
  SwitchInput,
  useFormContext,
} from "@4uhub/lib4uhub";
import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { busAutomationList } from "../../../../../services/busAutomation.service";
import { iProcesseList } from "../../../../../services/iProcesse.service";
import FormInformation from "../../../../../components/FormInformation/FormInformation";
import { TIntegrationRuleForm } from "./integrationRuleSchema";
import { useWatch } from "react-hook-form";

const translationPath = "page.4uBus.integrationRule.";
const physicalPersonSingleBaseAutomationCodes = [14, 15, 16, 17];

interface IBasicDataProps {
  disableAutomationEdit?: boolean;
}

export const BasicData = ({
  disableAutomationEdit = false,
}: IBasicDataProps) => {
  const { t } = useTranslation();

  const { id } = useParams();

  const { getValues, control, setValue } = useFormContext<TIntegrationRuleForm>();

  const uBusAutomation = useWatch({
    control,
    name: "uBusAutomation",
  }) as TIntegrationRuleForm["uBusAutomation"] | undefined;

  const showBatchConfiguration =
    uBusAutomation?.code !== undefined &&
    physicalPersonSingleBaseAutomationCodes.includes(uBusAutomation.code);

  useEffect(() => {
    if (!showBatchConfiguration) return;

    const batchSize = getValues("batchSize");
    const maxWorkers = getValues("maxWorkers");

    if (batchSize === undefined || batchSize === null) {
      setValue("batchSize", 50);
    }
    if (maxWorkers === undefined || maxWorkers === null) {
      setValue("maxWorkers", 5);
    }
  }, [getValues, setValue, showBatchConfiguration]);

  const uBusAutomationParams = useMemo(
    () => ({
      uBusAutomationCodeListRemove: [1],
    }),
    [],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} textAlign="end">
        <SwitchInput
          name="enable"
          label={t(translationPath + "enable") || "Enable"}
        />
      </Grid>

      {id && disableAutomationEdit ? (
        <Grid item xs={12} sm={6}>
          <FormInformation
            label={t(translationPath + "u_bus_automation")}
            data={getValues("uBusAutomation")?.name ?? ""}
            minHeight={40}
          />
        </Grid>
      ) : (
        <Grid item xs={12} sm={6}>
          <AutoComplete
            params={uBusAutomationParams}
            request={busAutomationList}
            name="uBusAutomation"
            label={t(translationPath + "u_bus_automation")}
            getOptionLabel={(option) => option.name}
            size="small"
            fullWidth
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <AutoComplete
          request={iProcesseList}
          name="iProcesse"
          label={t(translationPath + "i_processe")}
          getOptionLabel={(option) => option.name}
          size="small"
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormInput
          size="small"
          name="name"
          fullWidth
          label={t(translationPath + "name")}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <DateInput name="startDate" label={t(translationPath + "start_date")} />
      </Grid>

      <Grid item xs={12} sm={3}>
        <DateInput name="endDate" label={t(translationPath + "end_date")} />
      </Grid>

      <Grid item xs={12} sm={12}>
        <FormInput
          size="small"
          name="description"
          fullWidth
          multiline
          rows={4}
          label={t(translationPath + "description")}
        />
      </Grid>

      {uBusAutomation?.code === 19 && (
        <Grid item xs={12} sm={12}>
          <SwitchInput
            name="onlyRequestableLabExams"
            label={t(translationPath + "only_requestable_lab_exams")}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {t(translationPath + "only_requestable_lab_exams_tooltip")}
          </Typography>
        </Grid>
      )}

      {uBusAutomation?.code === 12 && (
        <Grid item xs={12} sm={6}>
          <FormInput
            size="small"
            name="suspensionReasonCode"
            fullWidth
            label={t(translationPath + "suspension_reason_code")}
          />
        </Grid>
      )}

      {showBatchConfiguration && (
        <>
          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="batchSize"
              type="number"
              fullWidth
              inputProps={{ min: 1, step: 1 }}
              label={t(translationPath + "batch_size")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormInput
              size="small"
              name="maxWorkers"
              type="number"
              fullWidth
              label={t(translationPath + "max_workers")}
            />
          </Grid>
        </>
      )}

      <Grid item sm={12}>
        <Typography fontWeight="bold">
          {t(translationPath + "filters")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <DateInput
          name="initialDate"
          label={t(translationPath + "initial_date")}
        />
      </Grid>
    </Grid>
  );
};
